import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card, Table, Button, Row, Col, Statistic, Tag, message, Modal, Form,
  Input, Select, InputNumber, Space, Tabs, Progress, Rate, Drawer,
  Alert, Divider, Avatar, Timeline, Badge, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  TrophyOutlined, TeamOutlined, BarChartOutlined, AimOutlined,
  WarningOutlined, UserOutlined, CheckCircleOutlined, StarOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';
import apiService from 'services/api/apiService';
import PerformanceReviewForm from '../../components/Performance/PerformanceReviewForm';
import ExportButtons from '../../components/ExportButtons';

const { TextArea } = Input;

const proficiencyColors: Record<string, string> = { novice: '#d9d9d9', developing: '#faad14', proficient: '#1890ff', advanced: '#52c41a', expert: '#722ed1' };
const kpiStatusColors: Record<string, string> = { not_started: 'default', in_progress: 'processing', achieved: 'success', missed: 'error', cancelled: 'warning' };
const pipStatusColors: Record<string, string> = { draft: 'default', active: 'processing', completed: 'success', failed: 'error', withdrawn: 'warning' };
const reviewerTypeColors: Record<string, string> = { self: 'blue', peer: 'green', subordinate: 'orange', manager: 'purple', external: 'cyan' };

const PerformanceManagement: React.FC = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<any[]>([]);
  const [kpis, setKpis] = useState<any[]>([]);
  const [feedback360, setFeedback360] = useState<any[]>([]);
  const [pips, setPips] = useState<any[]>([]);
  const [competencies, setCompetencies] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews');

  // Modals
  const [reviewFormVisible, setReviewFormVisible] = useState(false);
  const [kpiModal, setKpiModal] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [pipModal, setPipModal] = useState(false);
  const [competencyModal, setCompetencyModal] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editingReview, setEditingReview] = useState<any>(null);
  const [attEmployee, setAttEmployee] = useState<number | undefined>(undefined);
  const [attStart, setAttStart] = useState<string>(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10));
  const [attEnd, setAttEnd] = useState<string>(new Date().toISOString().slice(0, 10));
  const [attSummary, setAttSummary] = useState<any>(null);
  const [attRecords, setAttRecords] = useState<any[]>([]);
  const [attLoading, setAttLoading] = useState(false);

  const [kpiForm] = Form.useForm();
  const [feedbackForm] = Form.useForm();
  const [pipForm] = Form.useForm();
  const [competencyForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, [activeTab]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [er, emr, prr] = await Promise.all([
        apiService.getEmployees(),
        apiService.getPerformanceReviews(),
        apiService.getTrainingPrograms?.() || Promise.resolve({ success: false }),
      ]);
      if (er.success) setEmployees(er.data || []);
      if (emr.success) setReviews(emr.data || []);
      if (prr.success) setPrograms(prr.data || []);

      if (activeTab === 'kpi') {
        const r = await apiService.getKPIs();
        if (r.success) setKpis(r.data || []);
      }
      if (activeTab === '360') {
        const r = await apiService.get360Feedback();
        if (r.success) setFeedback360(r.data || []);
      }
      if (activeTab === 'pip') {
        const r = await apiService.getPIPs();
        if (r.success) setPips(r.data || []);
      }
      if (activeTab === 'competency') {
        const r = await apiService.getCompetencies();
        if (r.success) setCompetencies(r.data || []);
      }
    } finally { setLoading(false); }
  };

  const handleKPISubmit = async (values: any) => {
    const r = await apiService.createKPI(values);
    if (r.success) { message.success('KPI created'); setKpiModal(false); kpiForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handle360Submit = async (values: any) => {
    const r = await apiService.submit360Feedback(values);
    if (r.success) { message.success('360 feedback submitted'); setFeedbackModal(false); feedbackForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handlePIPSubmit = async (values: any) => {
    const r = await apiService.createPIP(values);
    if (r.success) { message.success('PIP created'); setPipModal(false); pipForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleCompetencySubmit = async (values: any) => {
    const r = await apiService.saveCompetency(values);
    if (r.success) { message.success('Competency saved'); setCompetencyModal(false); competencyForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const empOptions = employees.map(e => (
    <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>
  ));

  // ── Tab: Reviews ──────────────────────────────────────────────────────────
  const reviewsTab = (
    <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingReview(null); setReviewFormVisible(true); }}>New Review</Button>}>
      <Table dataSource={reviews} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
        columns={[
          { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => <><Avatar size="small" style={{ background: '#1890ff', marginRight: 8 }}>{v?.charAt(0)}</Avatar>{v || `${r.first_name} ${r.last_name}`}</> },
          { title: 'Period', dataIndex: 'review_period' },
          { title: 'Overall Rating', dataIndex: 'overall_rating', render: (v: number) => <Rate disabled defaultValue={v} count={5} style={{ fontSize: 14 }} /> },
          { title: 'Goals', dataIndex: 'goals_achievement', render: (v: number) => <Progress percent={v * 20} size="small" strokeColor={v >= 4 ? '#52c41a' : v >= 3 ? '#faad14' : '#ff4d4f'} format={() => `${v}/5`} /> },
          { title: 'Reviewer', dataIndex: 'reviewer_name' },
          { title: 'Date', dataIndex: 'review_date' },
          { title: 'Actions', render: (_: any, r: any) => (
            <Space>
              <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedItem(r); setDetailDrawer(true); }} />
              <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingReview(r); setReviewFormVisible(true); }} />
            </Space>
          )},
        ]} />
    </Card>
  );

  // ── Tab: KPI/OKR ─────────────────────────────────────────────────────────
  const kpiTab = (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="Total KPIs" value={kpis.length} prefix={<AimOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Achieved" value={kpis.filter(k => k.status === 'achieved').length} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="In Progress" value={kpis.filter(k => k.status === 'in_progress').length} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Missed" value={kpis.filter(k => k.status === 'missed').length} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
      </Row>
      <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setKpiModal(true)}>Add KPI/OKR</Button>}>
        <Table dataSource={kpis} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Employee', dataIndex: 'employee_name' },
            { title: 'Title', dataIndex: 'title' },
            { title: 'Type', dataIndex: 'kpi_type', render: (v: string) => <Tag color={v === 'okr' ? 'purple' : 'blue'}>{v?.toUpperCase()}</Tag> },
            { title: 'Category', dataIndex: 'category', render: (v: string) => <Tag>{v}</Tag> },
            { title: 'Progress', key: 'progress', render: (_: any, r: any) => {
              const pct = r.target_value ? Math.min(100, Math.round((r.actual_value / r.target_value) * 100)) : 0;
              return <Progress percent={pct} size="small" strokeColor={pct >= 100 ? '#52c41a' : pct >= 70 ? '#faad14' : '#1890ff'} format={() => `${r.actual_value}/${r.target_value} ${r.unit || ''}`} />;
            }},
            { title: 'Status', dataIndex: 'status', render: (v: string) => <Badge status={kpiStatusColors[v] as any} text={v?.replace('_', ' ')} /> },
            { title: 'Period', key: 'period', render: (_: any, r: any) => `${r.period_start || ''} → ${r.period_end || ''}` },
            { title: 'Actions', render: (_: any, r: any) => (
              <Space>
                <Button size="small" onClick={async () => {
                  const newStatus = r.status === 'in_progress' ? 'achieved' : 'in_progress';
                  await apiService.updateKPI({ id: r.id, title: r.title, actual_value: r.actual_value, status: newStatus, description: r.description });
                  fetchAll();
                }}>Update</Button>
              </Space>
            )},
          ]} />
      </Card>
    </div>
  );

  // ── Tab: 360 Feedback ─────────────────────────────────────────────────────
  const feedback360Tab = (
    <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setFeedbackModal(true)}>Submit 360 Feedback</Button>}>
      <Table dataSource={feedback360} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
        columns={[
          { title: 'Subject', dataIndex: 'subject_name' },
          { title: 'Reviewer', dataIndex: 'reviewer_name' },
          { title: 'Type', dataIndex: 'reviewer_type', render: (v: string) => <Tag color={reviewerTypeColors[v] || 'default'}>{v}</Tag> },
          { title: 'Period', dataIndex: 'review_period' },
          { title: 'Overall', dataIndex: 'overall_rating', render: (v: number) => v ? <Rate disabled defaultValue={v} count={5} style={{ fontSize: 12 }} /> : '-' },
          { title: 'Communication', dataIndex: 'communication', render: (v: number) => v ? `${v}/5` : '-' },
          { title: 'Teamwork', dataIndex: 'teamwork', render: (v: number) => v ? `${v}/5` : '-' },
          { title: 'Leadership', dataIndex: 'leadership', render: (v: number) => v ? `${v}/5` : '-' },
          { title: 'Submitted', dataIndex: 'submitted_at', render: (v: string) => v?.split('T')[0] },
        ]} />
    </Card>
  );

  // ── Tab: PIP ──────────────────────────────────────────────────────────────
  const pipTab = (
    <div>
      {pips.filter(p => p.status === 'active').length > 0 && (
        <Alert message={`${pips.filter(p => p.status === 'active').length} active Performance Improvement Plan(s)`} type="warning" showIcon style={{ marginBottom: 16 }} />
      )}
      <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setPipModal(true)}>Create PIP</Button>}>
        <Table dataSource={pips} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Employee', dataIndex: 'employee_name' },
            { title: 'Manager', dataIndex: 'manager_name' },
            { title: 'Title', dataIndex: 'title' },
            { title: 'Start Date', dataIndex: 'start_date' },
            { title: 'End Date', dataIndex: 'end_date' },
            { title: 'Days Left', dataIndex: 'days_remaining', render: (v: number) => v >= 0 ? <Tag color={v <= 7 ? 'error' : v <= 14 ? 'warning' : 'processing'}>{v} days</Tag> : <Tag color="default">Ended</Tag> },
            { title: 'Status', dataIndex: 'status', render: (v: string) => <Badge status={pipStatusColors[v] as any} text={v?.toUpperCase()} /> },
            { title: 'Actions', render: (_: any, r: any) => (
              <Space>
                <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedItem(r); setDetailDrawer(true); }} />
                {r.status === 'draft' && <Button size="small" type="primary" onClick={async () => { await apiService.updatePIP({ id: r.id, status: 'active' }); fetchAll(); }}>Activate</Button>}
                {r.status === 'active' && (
                  <Space>
                    <Button size="small" style={{ color: '#52c41a' }} onClick={async () => { await apiService.updatePIP({ id: r.id, status: 'completed', outcome: 'Employee met all PIP objectives' }); fetchAll(); }}>Complete</Button>
                    <Button size="small" danger onClick={async () => { await apiService.updatePIP({ id: r.id, status: 'failed', outcome: 'Employee did not meet PIP objectives' }); fetchAll(); }}>Failed</Button>
                  </Space>
                )}
              </Space>
            )},
          ]} />
      </Card>
    </div>
  );

  // ── Tab: Competency Framework ─────────────────────────────────────────────
  const competencyTab = (
    <div>
      <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCompetencyModal(true)}>Add Competency</Button>}>
        <Table dataSource={competencies} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.department} • {r.position}</div></> },
            { title: 'Competency', dataIndex: 'competency_name' },
            { title: 'Category', dataIndex: 'competency_category', render: (v: string) => <Tag>{v?.replace('_', ' ')}</Tag> },
            { title: 'Proficiency', dataIndex: 'proficiency_level', render: (v: string) => <Tag color={proficiencyColors[v]}>{v?.toUpperCase()}</Tag> },
            { title: 'Score', dataIndex: 'proficiency_score', render: (v: number) => <Progress percent={v} size="small" strokeColor={v >= 80 ? '#52c41a' : v >= 60 ? '#faad14' : '#ff4d4f'} /> },
            { title: 'Linked Training', dataIndex: 'linked_training_title', render: (v: string) => v ? <Tag color="blue" icon={<StarOutlined />}>{v}</Tag> : '-' },
            { title: 'Next Review', dataIndex: 'next_review_date' },
            { title: 'Actions', render: (_: any, r: any) => (
              <Button size="small" icon={<EditOutlined />} onClick={() => { competencyForm.setFieldsValue(r); setCompetencyModal(true); }} />
            )},
          ]} />
      </Card>
    </div>
  );

  const attColumns = [
    { key: 'attendance_date', label: 'Date' },
    { key: 'clock_in', label: 'Clock In' },
    { key: 'clock_out', label: 'Clock Out' },
    { key: 'status', label: 'Status' },
    { key: 'late_minutes', label: 'Late (mins)' },
    { key: 'hours_worked', label: 'Hours' },
    { key: 'overtime_hours', label: 'Overtime' },
    { key: 'clock_in_method', label: 'Method' },
  ];
  const attEmpName = () => { const e = employees.find((x: any) => x.id === attEmployee); return e ? `${e.first_name} ${e.last_name}` : ''; };
  const loadAttendance = async () => {
    if (!attEmployee) { message.warning('Select an employee'); return; }
    setAttLoading(true);
    try {
      const r = await apiService.getEmployeeAttendanceSummary(attEmployee, attStart, attEnd);
      if (r.success) { setAttSummary(r.summary); setAttRecords(r.records || []); }
      else message.error(r.error || 'Failed to load attendance');
    } finally { setAttLoading(false); }
  };
  const genAttendanceKPI = async () => {
    if (!attEmployee) { message.warning('Select an employee'); return; }
    const r = await apiService.generateAttendanceKPI(attEmployee, attStart, attEnd);
    if (r.success) message.success(`Attendance KPI generated: ${r.attendance_rate}% (${r.status})`);
    else message.error(r.error || 'Failed to generate KPI');
  };
  const attendanceTab = (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select showSearch placeholder="Select employee" optionFilterProp="children" style={{ minWidth: 260 }}
            value={attEmployee} onChange={(v) => setAttEmployee(v)}>{empOptions}</Select>
          <Input type="date" value={attStart} onChange={(e) => setAttStart(e.target.value)} style={{ width: 160 }} />
          <Input type="date" value={attEnd} onChange={(e) => setAttEnd(e.target.value)} style={{ width: 160 }} />
          <Button type="primary" loading={attLoading} onClick={loadAttendance}>Load</Button>
          <ExportButtons filename={`attendance_${attEmpName() || 'employee'}`} title={`Attendance - ${attEmpName()} (${attStart} to ${attEnd})`} columns={attColumns} rows={attRecords} />
          <Button onClick={genAttendanceKPI} disabled={!attSummary}>Generate Attendance KPI</Button>
        </Space>
      </Card>
      {attSummary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}><Card><Statistic title="Attendance %" value={attSummary.attendance_rate} suffix="%" valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Punctuality %" value={attSummary.punctuality_rate} suffix="%" valueStyle={{ color: '#1890ff' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Present" value={attSummary.days_present} /></Card></Col>
          <Col span={4}><Card><Statistic title="Late" value={attSummary.days_late} valueStyle={{ color: '#faad14' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Absent" value={attSummary.days_absent} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Late Mins" value={attSummary.total_late_minutes} /></Card></Col>
        </Row>
      )}
      <Card title="Daily Attendance">
        <Table dataSource={attRecords} rowKey={(r: any) => r.attendance_date} loading={attLoading} pagination={{ pageSize: 15 }}
          columns={attColumns.map((c) => ({ title: c.label, dataIndex: c.key }))} />
      </Card>
    </div>
  );

  const tabItems = [
    { key: 'reviews', label: '⭐ Reviews', children: reviewsTab },
    { key: 'kpi', label: '🎯 KPI / OKR', children: kpiTab },
    { key: '360', label: '🔄 360° Feedback', children: feedback360Tab },
    { key: 'pip', label: '⚠️ PIP', children: pipTab },
    { key: 'competency', label: '🧠 Competencies', children: competencyTab },
    { key: 'attendance', label: 'Attendance', children: attendanceTab },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Performance Management</h1>
      <Tabs activeKey={activeTab} onChange={k => { setActiveTab(k); }} items={tabItems} />

      {/* Review Form Modal */}
      <Modal title={editingReview ? 'Edit Review' : 'New Performance Review'} open={reviewFormVisible}
        onCancel={() => { setReviewFormVisible(false); setEditingReview(null); }} footer={null} width={800}>
        <PerformanceReviewForm review={editingReview} onSuccess={() => { setReviewFormVisible(false); fetchAll(); }}
          onCancel={() => { setReviewFormVisible(false); setEditingReview(null); }} />
      </Modal>

      {/* KPI Modal */}
      <Modal title="Create KPI / OKR" open={kpiModal} onCancel={() => { setKpiModal(false); kpiForm.resetFields(); }} footer={null} width={650}>
        <Form form={kpiForm} layout="vertical" onFinish={handleKPISubmit}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="kpi_type" label="Type" initialValue="kpi" rules={[{ required: true }]}><Select><Select.Option value="kpi">KPI</Select.Option><Select.Option value="okr">OKR</Select.Option><Select.Option value="target">Target</Select.Option></Select></Form.Item></Col>
          </Row>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}><Input placeholder="e.g. Increase sales revenue by 20%" /></Form.Item>
          <Form.Item name="description" label="Description"><TextArea rows={2} /></Form.Item>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="category" label="Category" initialValue="other"><Select><Select.Option value="financial">Financial</Select.Option><Select.Option value="customer">Customer</Select.Option><Select.Option value="process">Process</Select.Option><Select.Option value="learning">Learning</Select.Option><Select.Option value="other">Other</Select.Option></Select></Form.Item></Col>
            <Col span={8}><Form.Item name="target_value" label="Target Value" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="unit" label="Unit"><Input placeholder="%, ₦, units, etc." /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="weight" label="Weight (%)" initialValue={100}><InputNumber min={1} max={100} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="period_start" label="Start Date"><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="period_end" label="End Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="review_cycle" label="Review Cycle" initialValue="quarterly"><Select><Select.Option value="monthly">Monthly</Select.Option><Select.Option value="quarterly">Quarterly</Select.Option><Select.Option value="biannual">Bi-Annual</Select.Option><Select.Option value="annual">Annual</Select.Option></Select></Form.Item></Col>
            <Col span={12}><Form.Item name="status" label="Status" initialValue="not_started"><Select><Select.Option value="not_started">Not Started</Select.Option><Select.Option value="in_progress">In Progress</Select.Option></Select></Form.Item></Col>
          </Row>
          <Space><Button type="primary" htmlType="submit">Create KPI</Button><Button onClick={() => setKpiModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* 360 Feedback Modal */}
      <Modal title="Submit 360° Feedback" open={feedbackModal} onCancel={() => { setFeedbackModal(false); feedbackForm.resetFields(); }} footer={null} width={650}>
        <Form form={feedbackForm} layout="vertical" onFinish={handle360Submit} initialValues={{ is_anonymous: 1 }}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="subject_employee_id" label="Employee Being Reviewed" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="reviewer_employee_id" label="Reviewer (You)" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="reviewer_type" label="Reviewer Relationship" rules={[{ required: true }]}><Select><Select.Option value="self">Self</Select.Option><Select.Option value="peer">Peer</Select.Option><Select.Option value="subordinate">Subordinate</Select.Option><Select.Option value="manager">Manager</Select.Option><Select.Option value="external">External</Select.Option></Select></Form.Item></Col>
            <Col span={12}><Form.Item name="review_period" label="Review Period" rules={[{ required: true }]}><Input placeholder="e.g. Q2 2026" /></Form.Item></Col>
          </Row>
          <Divider>Ratings (1-5)</Divider>
          <Row gutter={16}>
            {[['communication','Communication'],['teamwork','Teamwork'],['leadership','Leadership'],['technical_skills','Technical Skills'],['problem_solving','Problem Solving'],['initiative','Initiative']].map(([k,l]) => (
              <Col span={8} key={k}><Form.Item name={k} label={l}><InputNumber min={1} max={5} style={{ width: '100%' }} addonAfter="/5" /></Form.Item></Col>
            ))}
          </Row>
          <Form.Item name="overall_rating" label="Overall Rating (1-5)" rules={[{ required: true }]}><InputNumber min={1} max={5} style={{ width: '50%' }} /></Form.Item>
          <Form.Item name="strengths" label="Key Strengths"><TextArea rows={2} /></Form.Item>
          <Form.Item name="improvements" label="Areas for Improvement"><TextArea rows={2} /></Form.Item>
          <Form.Item name="comments" label="Additional Comments"><TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Submit Feedback</Button><Button onClick={() => setFeedbackModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* PIP Modal */}
      <Modal title="Create Performance Improvement Plan" open={pipModal} onCancel={() => { setPipModal(false); pipForm.resetFields(); }} footer={null} width={700}>
        <Alert message="A PIP is a formal document to help an employee improve performance. Use only after informal coaching has been tried." type="warning" showIcon style={{ marginBottom: 16 }} />
        <Form form={pipForm} layout="vertical" onFinish={handlePIPSubmit}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="manager_id" label="Manager"><Select showSearch optionFilterProp="children" allowClear>{empOptions}</Select></Form.Item></Col>
          </Row>
          <Form.Item name="title" label="PIP Title" rules={[{ required: true }]}><Input placeholder="e.g. Performance Improvement Plan — Sales Targets" /></Form.Item>
          <Form.Item name="reason" label="Reason for PIP" rules={[{ required: true }]}><TextArea rows={3} placeholder="Describe the performance issues that have led to this PIP..." /></Form.Item>
          <Form.Item name="objectives" label="SMART Objectives" rules={[{ required: true }]}><TextArea rows={4} placeholder="List specific, measurable objectives the employee must achieve..." /></Form.Item>
          <Form.Item name="support_provided" label="Support to be Provided"><TextArea rows={2} placeholder="Training, coaching, resources available to the employee..." /></Form.Item>
          <Form.Item name="consequences" label="Consequences of Not Meeting Objectives"><TextArea rows={2} placeholder="e.g. Further disciplinary action up to and including termination..." /></Form.Item>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="review_date" label="Review Date"><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="end_date" label="End Date" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
          </Row>
          <Space><Button type="primary" htmlType="submit">Create PIP</Button><Button onClick={() => setPipModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Competency Modal */}
      <Modal title="Add / Update Competency" open={competencyModal} onCancel={() => { setCompetencyModal(false); competencyForm.resetFields(); }} footer={null} width={650}>
        <Form form={competencyForm} layout="vertical" onFinish={handleCompetencySubmit}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="assessed_by" label="Assessed By"><Select showSearch optionFilterProp="children" allowClear>{empOptions}</Select></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="competency_name" label="Competency" rules={[{ required: true }]}><Input placeholder="e.g. ISO 27001 Implementation" /></Form.Item></Col>
            <Col span={12}><Form.Item name="competency_category" label="Category" initialValue="technical"><Select><Select.Option value="technical">Technical</Select.Option><Select.Option value="leadership">Leadership</Select.Option><Select.Option value="communication">Communication</Select.Option><Select.Option value="teamwork">Teamwork</Select.Option><Select.Option value="problem_solving">Problem Solving</Select.Option><Select.Option value="customer_focus">Customer Focus</Select.Option><Select.Option value="other">Other</Select.Option></Select></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="proficiency_level" label="Proficiency Level" initialValue="developing"><Select>{['novice','developing','proficient','advanced','expert'].map(l => <Select.Option key={l} value={l}><Tag color={proficiencyColors[l]}>{l}</Tag></Select.Option>)}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="proficiency_score" label="Score (0-100)" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="linked_training_id" label="Linked Training Program"><Select allowClear showSearch optionFilterProp="children">{programs.map((p: any) => <Select.Option key={p.id} value={p.program_id}>{p.title}</Select.Option>)}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="next_review_date" label="Next Review Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Form.Item name="development_plan" label="Development Plan"><TextArea rows={3} placeholder="Actions to improve this competency..." /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Save Competency</Button><Button onClick={() => setCompetencyModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer title="Details" open={detailDrawer} onClose={() => setDetailDrawer(false)} width={500}>
        {selectedItem && (
          <div>
            {selectedItem.objectives && (
              <>
                <Divider>PIP: {selectedItem.title}</Divider>
                <p><strong>Employee:</strong> {selectedItem.employee_name}</p>
                <p><strong>Reason:</strong> {selectedItem.reason}</p>
                <p><strong>Objectives:</strong> {selectedItem.objectives}</p>
                <p><strong>Support:</strong> {selectedItem.support_provided}</p>
                <p><strong>Consequences:</strong> {selectedItem.consequences}</p>
                <p><strong>Period:</strong> {selectedItem.start_date} → {selectedItem.end_date}</p>
                <Tag color={pipStatusColors[selectedItem.status]}>{selectedItem.status?.toUpperCase()}</Tag>
              </>
            )}
            {selectedItem.overall_rating !== undefined && !selectedItem.objectives && (
              <>
                <Divider>Performance Review</Divider>
                <p><strong>Period:</strong> {selectedItem.review_period}</p>
                <p><strong>Overall:</strong> <Rate disabled defaultValue={selectedItem.overall_rating} count={5} /></p>
                <p><strong>Goals:</strong> {selectedItem.goals_achievement}/5</p>
                <p><strong>Comments:</strong> {selectedItem.comments}</p>
                <p><strong>Achievements:</strong> {selectedItem.achievements}</p>
                <p><strong>Next Goals:</strong> {selectedItem.goals_next_period}</p>
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default PerformanceManagement;
